import {
  Application,
  Container,
  Graphics,
  Sprite,
  Texture,
} from 'pixi.js';
import HitCircle from '../../renderers/HitCircle';
import Theme from '../Theme';
import type { HitCircle } from '@musicgame/chart';
import { preemptMs, fadeInMs } from '@musicgame/chart';
import { GameClock } from '../../timing';
import { circleRadius, followRadius } from './radius';
import { isModActive, applyAR } from '../../mods.js';
import {
  buildPathTable,
  generateTicks,
  computeEndTime,
  type BPMChange,
} from '../../utils/pathLinker';
import type { Point } from '../../utils/distanceMap';
import { decodePathCommands, tessellateCommands } from '../../utils/pathLinker';
import { pointsToCommands } from '../../utils/coalesceSliderChains';
import { TOKENS } from '../../tokens';

const STACK_OFFSET = 6; // pixels per stack level

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Basic circle-style theme implemented with PixiJS. Notes appear as circles that
 * shrink toward a receptor circle and must be clicked within a timing window.
 */
export default class CircleTheme implements Theme<HitCircle> {
  private difficulty = { cs: 3, sv: 1 };
  private flashlightMask: Graphics | null = null;
  private flashlightPos = { x: 0, y: 0 };
  private app: Application | null = null;

  setDifficulty(diff: Partial<{ cs: number; sv: number }>): void {
    this.difficulty = { ...this.difficulty, ...diff };
    this.drawFlashlightMask();
  }
  init(app: Application): void {
    this.app = app;
    // circle-style charts don't have static lanes or receptors.
  }

  registerInputs(app: Application): void {
    this.app = app;
    // MainScene handles input; nothing required here but keep stage interactive.
    app.stage.eventMode = 'static';

    if (isModActive('flashlight')) {
      app.stage.sortableChildren = true;
      this.flashlightMask = new Graphics();
      this.flashlightMask.zIndex = 1000;
      app.stage.addChild(this.flashlightMask);
      this.flashlightPos = {
        x: app.screen.width / 2,
        y: app.screen.height / 2,
      };
      this.drawFlashlightMask();
      app.stage.on('pointermove', (e) => {
        this.flashlightPos = { x: e.global.x, y: e.global.y };
        this.drawFlashlightMask();
      });
    }
  }

  private drawFlashlightMask(): void {
    if (!this.flashlightMask || !this.app) return;
    const radius = circleRadius(this.difficulty.cs) + STACK_OFFSET;
    const mask = this.flashlightMask;
    const { width, height } = this.app.screen;
    mask.clear();
    mask
      .rect(0, 0, width, height)
      .fill({ color: 0x000000, alpha: 0.85 });
    mask
      .circle(this.flashlightPos.x, this.flashlightPos.y, radius)
      .fill({ color: 0x000000, alpha: 1, hole: true });
  }

  private timing(approachRate: number): {
    preempt: number;
    fade: number;
    hidden: boolean;
  } {
    return {
      preempt: preemptMs(approachRate),
      fade: fadeInMs(approachRate),
      hidden: isModActive('hidden'),
    };
  }

  renderNote(app: Application, note: any, approachRate = note.approachRate): any {
    approachRate = applyAR(approachRate);
    const isSlider = Array.isArray(note.path)
      || Array.isArray(note.points)
      || Array.isArray(note.pts);
    const isSpinner = typeof note.tEnd === 'number' && !isSlider;
    if (isSlider) return this.renderSlider(app, note, approachRate);
    if (isSpinner) return this.renderSpinner(app, note, approachRate);
    // Default to hit circle.
    const offset = (note.stack ?? 0) * STACK_OFFSET;
    const hr = isModActive('hardRock');
    const x = (note.pos.x / 9) * app.screen.width + offset;
    const rawY = hr ? 16 - note.pos.y : note.pos.y;
    const y = (rawY / 16) * app.screen.height + offset;

    const cs = this.difficulty.cs;
    const cRadius = circleRadius(cs);
    const fRadius = followRadius(cs);
    const { preempt, fade, hidden } = this.timing(approachRate);

    const circle = new HitCircle(
      app,
      x,
      y,
      cRadius,
      fRadius,
      preempt,
      fade,
      hidden,
    );
    app.stage.addChild(circle);
    return circle;
  }

  /** Render a slider with cached geometry and visual effects. */
  private renderSlider(
    app: Application,
    note: any,
    approachRate: number,
  ): { note: Container; preview: Container } {
    const cs = this.difficulty.cs;
    const cRadius = circleRadius(cs);
    const fRadius = followRadius(cs);
    const startScale = fRadius / cRadius;
    const { preempt, fade, hidden } = this.timing(approachRate);

    const offset = (note.stack ?? 0) * STACK_OFFSET;
    const hr = isModActive('hardRock');

    // Decode path commands then scale control points to stage coordinates.
    const cmds = Array.isArray(note.path)
      ? decodePathCommands(note.path)
      : (() => {
          const pts: any[] = Array.isArray(note.points)
            ? note.points
            : Array.isArray(note.pts)
              ? note.pts
              : [];
          return pts.length === 0 ? [] : pointsToCommands(pts);
        })();

    const scaledCmds = cmds.map((c: any) => {
      const map = (x: number, y: number) => {
        const py = hr ? 16 - y : y;
        return {
          x: (x / 9) * app.screen.width + offset,
          y: (py / 16) * app.screen.height + offset,
        };
      };
      if (c.cmd === 'B') {
        const cp = map(c.cx, c.cy);
        const end = map(c.x, c.y);
        return { cmd: 'B', cx: cp.x, cy: cp.y, x: end.x, y: end.y };
      }
      const pt = map(c.x, c.y);
      return { cmd: c.cmd, x: pt.x, y: pt.y };
    });

    const polyline: Point[] = tessellateCommands(scaledCmds);

    // --- debug guard for degenerate preview paths ---
    const screen = { width: app.screen.width, height: app.screen.height };
    // Extremely small screens can produce 1‑2px paths after scaling.
    if (screen.width < 8 || screen.height < 8) {
      console.warn('Slider preview screen is very small', {
        screen,
        path: note.path ?? note.points ?? note.pts,
      });
    }

    if (polyline.length < 2) {
      // Path with fewer than two points will render as a single pixel.
      console.warn('Slider preview path has insufficient points', {
        path: note.path ?? note.points ?? note.pts,
      });
    } else {
      let pathLen = 0;
      for (let i = 1; i < polyline.length; i++) {
        const a = polyline[i - 1];
        const b = polyline[i];
        pathLen += Math.hypot(b.x - a.x, b.y - a.y);
      }
      if (pathLen <= 1) {
        // Log additional info to track down unexpected 1px sliders.
        console.warn('Slider preview path length ≤ 1px', {
          len: pathLen,
          path: note.path ?? note.points ?? note.pts,
          screen,
        });
      } else if (pathLen <= 4) {
        // Catch suspiciously short paths that previously slipped past the 1px check.
        console.warn('Slider preview path length unusually short', {
          len: pathLen,
          path: note.path ?? note.points ?? note.pts,
          screen,
        });
      }
    }

    let pixelLength = note.pixelLength;
    if (pixelLength == null) {
      pixelLength = 0;
      for (let i = 1; i < polyline.length; i++) {
        const a = polyline[i - 1];
        const b = polyline[i];
        pixelLength += Math.hypot(b.x - a.x, b.y - a.y);
      }
    }

    const repeats = Math.max(1, Number(note.repeats) || 0);
    const tEnd =
      typeof note.tEnd === 'number'
        ? note.tEnd
        : typeof note.duration === 'number'
        ? note.tStart + note.duration
        : computeEndTime(
            note.tStart,
            pixelLength * repeats,
            note.sv ?? this.difficulty.sv,
            note.bpmChanges as BPMChange[],
          );
    const duration = tEnd - note.tStart;
    const table = buildPathTable(polyline, duration, repeats, true);

    // Tick and repeat times
    const allTicks = generateTicks(
      note.tStart,
      duration,
      note.beatDivisor ?? 1,
      repeats,
      note.bpmChanges as BPMChange[],
    );
    const runDuration = duration / repeats;
    const tickTimes = allTicks.filter((t) => !isRepeatTime(t, runDuration));
    const repeatTimes = allTicks.filter((t) => isRepeatTime(t, runDuration));

    // Interaction event times including start, ticks, repeats and end
    const events = [
      { time: 0, type: 'start' },
      ...allTicks.map((t) => ({
        time: t,
        type: isRepeatTime(t, runDuration) ? 'repeat' : 'tick',
      })),
      { time: duration, type: 'end' },
    ];

    const accent2 = parseInt(TOKENS.accent2.slice(1), 16);

    // Preview track with rim
    const previewTrack = new Graphics();
    previewTrack.moveTo(polyline[0].x, polyline[0].y);
    for (let i = 1; i < polyline.length; i++) {
      previewTrack.lineTo(polyline[i].x, polyline[i].y);
    }
    previewTrack.stroke({ width: cRadius * 2, color: accent2, alpha: 0.2 });
    previewTrack.alpha = 0;

    // Path line
    const path = new Graphics();
    path.moveTo(polyline[0].x, polyline[0].y);
    for (let i = 1; i < polyline.length; i++) path.lineTo(polyline[i].x, polyline[i].y);
    path.stroke({ width: 3, color: accent2, alpha: 0.6 });
    path.alpha = 0;

    // Tick markers as small dashes perpendicular to path
    const tickG = new Graphics();
    const dash = 6;
    for (const t of tickTimes) {
      const p = table.pointAt(t);
      const prev = table.pointAt(Math.max(t - 1, 0));
      const next = table.pointAt(Math.min(t + 1, duration));
      const dx = next.x - prev.x;
      const dy = next.y - prev.y;
      const len = Math.hypot(dx, dy) || 1;
      const nx = -dy / len;
      const ny = dx / len;
      tickG.moveTo(p.x - (nx * dash) / 2, p.y - (ny * dash) / 2);
      tickG.lineTo(p.x + (nx * dash) / 2, p.y + (ny * dash) / 2);
    }
    tickG.stroke({ width: 2, color: accent2, alpha: 0.35 });
    tickG.alpha = 0;

    // Repeat arrows with burst rings
    const repeatMarkers: {
      arrow: Graphics;
      ring: Graphics;
      time: number;
      fadeStart: number | null;
      burstStart: number | null;
      triggered: boolean;
    }[] = [];
    for (const t of repeatTimes) {
      const p = table.pointAt(t);
      const prev = table.pointAt(Math.max(t - 1, 0));
      const next = table.pointAt(Math.min(t + 1, duration));
      const ang = Math.atan2(next.y - prev.y, next.x - prev.x);
      const size = cRadius * 0.6;
      const arrow = new Graphics();
      arrow
        .moveTo(-size * 0.5, -size)
        .lineTo(size * 0.5, 0)
        .lineTo(-size * 0.5, size)
        .lineTo(-size * 0.5, -size)
        .closePath()
        .fill({ color: accent2 })
        .stroke({ width: 2, color: 0xffffff });
      arrow.rotation = ang;
      arrow.position.set(p.x, p.y);
      arrow.alpha = 0;

      const ring = new Graphics();
      ring.circle(0, 0, cRadius * 0.8).stroke({ width: 2, color: 0xffffff });
      ring.position.set(p.x, p.y);
      ring.alpha = 0;

      repeatMarkers.push({
        arrow,
        ring,
        time: t,
        fadeStart: null,
        burstStart: null,
        triggered: false,
      });
    }

    // Start circle and shrinking ring
    const startCircle = new Graphics();
    startCircle.circle(0, 0, cRadius).fill({ color: 0xffffff, alpha: 0.8 });
    startCircle.position.set(polyline[0].x, polyline[0].y);
    startCircle.alpha = 0;

    const ring = new Graphics();
    ring.circle(0, 0, cRadius).stroke({ width: 2, color: 0xffffff });
    ring.position.set(polyline[0].x, polyline[0].y);
    ring.scale.set(startScale);

    // Slider ball with rim and direction arrow
    const ball = new Container();
    const ballBody = new Graphics();
    ballBody.circle(0, 0, cRadius).fill({ color: accent2 });
    const ballRim = new Graphics();
    ballRim.circle(0, 0, cRadius).stroke({ width: 2, color: 0xffffff });
    const arrow = new Graphics();
    arrow
      .moveTo(cRadius * 0.6, 0)
      .lineTo(-cRadius * 0.3, cRadius * 0.3)
      .lineTo(-cRadius * 0.3, -cRadius * 0.3)
      .lineTo(cRadius * 0.6, 0)
      .closePath()
      .fill({ color: 0xffffff });
    ball.addChild(ballBody);
    ball.addChild(ballRim);
    ball.addChild(arrow);
    ball.blendMode = 'add';
    const startPos = table.pointAt(0);
    const nextPos = table.pointAt(Math.min(1, duration));
    ball.position.set(startPos.x, startPos.y);
    arrow.rotation = Math.atan2(nextPos.y - startPos.y, nextPos.x - startPos.x);

    const followCircle = new Graphics();
    followCircle
      .circle(0, 0, fRadius)
      .stroke({ width: 4, color: 0xffffff, alpha: 0.25 });
    followCircle.position.set(startPos.x, startPos.y);
    followCircle.alpha = 0;

    // Comet trail container
    const trailLayer = new Container();

    const appear = GameClock.now();
    const ticker = () => {
      const now = GameClock.now();
      const elapsed = now - appear;
      let alpha = Math.min(elapsed / fade, 1);
      if (hidden) {
        const fadeStart = preempt - fade;
        if (elapsed >= fadeStart) {
          alpha = Math.max(1 - (elapsed - fadeStart) / fade, 0);
        }
      }
      path.alpha = alpha;
      tickG.alpha = alpha;
      previewTrack.alpha = alpha;
      startCircle.alpha = alpha;
      ring.alpha = alpha;
      for (const m of repeatMarkers) m.arrow.alpha = alpha;

      const shrinkProgress = Math.min(elapsed / preempt, 1);
      const scale = startScale - (startScale - 1) * easeOutCubic(shrinkProgress);
      ring.scale.set(scale);
      if (elapsed >= preempt) {
        ring.destroy();
        startCircle.destroy();
        app.ticker.remove(ticker);
      }
    };
    app.ticker.add(ticker);

    let lastPos = startPos;
    let followFadeStart: number | null = hidden ? note.tStart : null;
    const ballTicker = () => {
      const now = GameClock.now();
      const t = now - note.tStart;
      if (t < 0) return;
      const clamped = Math.min(t, duration);
      const p = table.pointAt(clamped);
      ball.position.set(p.x, p.y);
      followCircle.position.set(p.x, p.y);

      // Comet trail quad
      const dx = p.x - lastPos.x;
      const dy = p.y - lastPos.y;
      const dist = Math.hypot(dx, dy);
      if (dist > 1) {
        const quad = new Sprite(Texture.WHITE);
        quad.tint = accent2;
        quad.blendMode = 'add';
        quad.anchor.set(0, 0.5);
        quad.position.set(lastPos.x, lastPos.y);
        quad.rotation = Math.atan2(dy, dx);
        quad.width = dist;
        quad.height = cRadius * 0.6;
        (quad as any).birth = now;
        quad.alpha = 0.6;
        trailLayer.addChild(quad);
      }
      lastPos = { x: p.x, y: p.y };

      // Arrow direction
      const prev = table.pointAt(Math.max(clamped - 1, 0));
      const next = table.pointAt(Math.min(clamped + 1, duration));
      arrow.rotation = Math.atan2(next.y - prev.y, next.x - prev.x);

      for (const child of [...trailLayer.children]) {
        const age = now - (child as any).birth;
        child.alpha = 0.6 * Math.max(1 - age / 180, 0);
        if (age >= 180) child.destroy();
      }

      // Repeat arrows fade and burst rings
      for (const m of repeatMarkers) {
        if (t >= m.time && !m.triggered) {
          m.triggered = true;
          m.burstStart = now;
          m.ring.alpha = 1;
          m.ring.scale.set(1);
        }
        if (t >= m.time) {
          if (m.fadeStart === null) m.fadeStart = now;
          const progress = (now - m.fadeStart) / fade;
          m.arrow.alpha = Math.max(1 - progress, 0);
        }
        if (m.burstStart !== null) {
          const age = now - m.burstStart;
          if (m.ring.scale) {
            m.ring.alpha = Math.max(1 - age / 180, 0);
            m.ring.scale.set(1 + age / 180);
          }
          if (age >= 180) {
            if (m.ring.parent) m.ring.destroy();
            m.burstStart = null;
          }
        }
      }
      for (let i = repeatMarkers.length - 1; i >= 0; i--) {
        const m = repeatMarkers[i];
        if (m.fadeStart !== null && now - m.fadeStart >= fade) {
          m.arrow.destroy();
          repeatMarkers.splice(i, 1);
        }
      }

      let fcAlpha = 0.3;
      if (followFadeStart !== null) {
        const progress = (now - followFadeStart) / fade;
        fcAlpha = 0.3 * Math.max(1 - progress, 0);
        if (progress >= 1 && followCircle.parent) followCircle.destroy();
      }
      followCircle.alpha = fcAlpha;

      if (!hidden && t >= duration && followFadeStart === null) {
        followFadeStart = now;
      }

      if (t >= duration) {
        for (const m of repeatMarkers) m.arrow.destroy();
        if (followFadeStart !== null && now - followFadeStart >= fade) {
          app.ticker.remove(ballTicker);
        }
      }
    };
    app.ticker.add(ballTicker);

    // Split preview and interactive layers
    const preview = new Container();
    preview.addChild(previewTrack);
    preview.addChild(path);
    preview.addChild(tickG);
    for (const m of repeatMarkers) {
      preview.addChild(m.arrow);
      preview.addChild(m.ring);
    }

    const sliderContainer = new Container() as any;
    sliderContainer.addChild(startCircle);
    sliderContainer.addChild(ring);
    sliderContainer.addChild(trailLayer);
    sliderContainer.addChild(followCircle);
    sliderContainer.addChild(ball);
    sliderContainer.table = table;
    sliderContainer.followRadius = fRadius;
    sliderContainer.tEnd = tEnd;
    sliderContainer.duration = duration;
    sliderContainer.polyline = polyline;
    sliderContainer.events = events;

    app.stage.addChild(preview);
    app.stage.addChild(sliderContainer);

    return { note: sliderContainer, preview };
  }

  /** Render a simple spinner disc. */
  private renderSpinner(app: Application, note: any, approachRate: number): Graphics {
    const { preempt, fade } = this.timing(approachRate);
    const offset = (note.stack ?? 0) * STACK_OFFSET;
    const hr = isModActive('hardRock');
    const x = (note.pos.x / 9) * app.screen.width + offset;
    const rawY = hr ? 16 - note.pos.y : note.pos.y;
    const y = (rawY / 16) * app.screen.height + offset;
    const radius = followRadius(this.difficulty.cs) * 1.5;

    const disc = new Graphics();
    disc.circle(0, 0, radius).stroke({ width: 4, color: 0xffffff });
    disc.x = x;
    disc.y = y;
    disc.alpha = 0;
    (disc as any).radius = radius;
    app.stage.addChild(disc);

    const ring = new Graphics();
    ring.circle(0, 0, radius).stroke({ width: 4, color: 0xffffff });
    ring.x = x;
    ring.y = y;
    const startScale = 2;
    ring.scale.set(startScale);
    ring.alpha = 0;
    app.stage.addChild(ring);

    const appear = GameClock.now();
    let fadeOutStart: number | null = null;
    const ticker = () => {
      const now = GameClock.now();
      const elapsed = now - appear;
      if (now < note.tStart) {
        const alpha = Math.min(elapsed / fade, 1);
        disc.alpha = 0.8 * alpha;
        ring.alpha = alpha;
        const shrink = Math.min(elapsed / preempt, 1);
        ring.scale.set(startScale - (startScale - 1) * shrink);
      } else {
        if (ring.parent) ring.destroy();
        if (fadeOutStart === null) fadeOutStart = now;
        const fadeElapsed = now - fadeOutStart;
        disc.alpha = 0.8 * Math.max(1 - fadeElapsed / fade, 0);
        if (fadeElapsed >= fade) {
          disc.destroy();
          app.ticker.remove(ticker);
        }
      }
    };
    app.ticker.add(ticker);

    return disc;
  }
}

// Helper to determine if time hits a repeat boundary.
function isRepeatTime(t: number, runDuration: number): boolean {
  if (!isFinite(runDuration) || runDuration <= 0) return false;
  const mod = t % runDuration;
  return Math.abs(mod) < 1e-3 || Math.abs(mod - runDuration) < 1e-3;
}

